import type { UploadedFiles } from '../../../../../types/Shared';
import { formatDate3 } from '../../../../../utils';
import { CurrencyFormatDisplay } from '../../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { PhoneNumberFormatDisplay } from '../../../../UI/NumberFormatDisplay/PhoneNumberFormatDisplay';

export default function ApplicantApplication({ organizationInformation, files }: { organizationInformation: any; files: UploadedFiles[] }) {
  return (
    <>
      {organizationInformation && (
        <section aria-labelledby='applicant-information-title'>
          <div className='bg-white shadow sm:rounded-lg'>
            <div className='py-5'>
              <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
                <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
                  Application Information
                </h2>
                <p className='mt-1 max-w-2xl text-sm text-gray-500'>Details of an application.</p>
              </div>
            </div>
            <div className='border-t border-gray-200 px-4 py-5 sm:px-6 space-y-5'>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Business Name</dt>
                  <dd className='mt-1 text-sm text-gray-900'>{organizationInformation.businessName}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Business Address</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.businessAddress}, {organizationInformation.businessCity}, {organizationInformation.state},{' '}
                    {organizationInformation.zipCode}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Date Established</dt>
                  <dd className='mt-1 text-sm text-gray-900'>{formatDate3(organizationInformation.establishedDate)}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Legal Status</dt>
                  <dd className='mt-1 text-sm text-gray-900 uppercase'>{organizationInformation.legalStatus}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Business Phone</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    <PhoneNumberFormatDisplay value={organizationInformation.businessPhone} />
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Lead Contact</dt>
                  <dd className='mt-1 text-sm text-gray-900 flex flex-col'>{organizationInformation.leadContact}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Contact Information</dt>
                  <dd className='mt-1 text-sm text-gray-900 flex flex-col'>
                    <span>{organizationInformation.email}</span>
                    <span>
                      <PhoneNumberFormatDisplay value={organizationInformation.phone} />
                    </span>
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Website</dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {organizationInformation.website ? (
                      <a
                        href={`https://${organizationInformation.website}`}
                        className='underline text-blue-500'
                        target='_blank'
                        rel='noreferrer'
                      >
                        {organizationInformation.website}
                      </a>
                    ) : (
                      <span>None</span>
                    )}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Years in development</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation?.yearsInDevelopment}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Own or Operate any affordable properties</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation?.ownOperateAffordableProperties}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>HAND Member</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation?.isHandMember}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Worked with Greystone in the past</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation?.haveWorkedWithGreystone}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>How can Equity in Action help</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation?.canHelp}</dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Capital requested</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    <CurrencyFormatDisplay value={organizationInformation.capitalAmount} prefix='$' />
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>How else can Greystone help ?</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    {organizationInformation?.greystoneHelp?.map((gh: string) => `${gh}, `)}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>Social Designation</dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>{organizationInformation.socialDesignation}</dd>
                </div>
              </dl>
              <div className='space-y-2'>
                <h3>Sponsors</h3>
                <dl className='space-y-5'>
                  {organizationInformation.sponsors.map((sponsor: any) => {
                    return (
                      <div key={sponsor.socialSecurity} className='grid grid-cols-2 gap-5 border p-4 rounded-md'>
                        <div>
                          <dt className='text-neutralColor-normal'>Information</dt>
                          <dd className='flex flex-col'>
                            <span>
                              {sponsor.name} ({sponsor.ownershipPercent}
                              %)
                            </span>
                            <span>{sponsor.address}</span>
                            <span>
                              {sponsor.city}, {sponsor.state}, {sponsor.zipCode}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className='text-neutralColor-normal'>Contact Information</dt>
                          <dd className='flex flex-col'>
                            <span className='truncate'>{sponsor.leadContact}</span>
                            <span className='truncate'>{sponsor.email}</span>
                            <span>
                              <PhoneNumberFormatDisplay value={sponsor.phone} />
                            </span>
                          </dd>
                        </div>
                      </div>
                    );
                  })}
                </dl>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
